// ─── "Unlock your chat" — new-device modal (CHAT_E2E_DESIGN.md §3, §8) ────────
// Shown when a public key already exists for this user (chat was set up on
// some device, at some point) but THIS device/browser has no local copy of
// the private key — a new device, or browser data was cleared. Prompts for
// the Chat Recovery Passphrase, decrypts the backed-up private key entirely
// client-side, and caches it locally so this device won't ask again.
//
// A wrong passphrase fails the local decrypt with no server round-trip
// needed to know it was wrong (ChatIdentityContext.completeUnlock already
// fetched the encrypted backup; everything past that point is local math),
// so there's no oracle here that could help time or otherwise distinguish
// "wrong passphrase" from anything else.
//
// ── "Forgotten your passphrase?" reset flow ─────────────────────────────────
// Originally this modal was a dead end if the passphrase was genuinely lost
// (or — a real bug found in production — if chatKeys/{uid} got created but
// chatKeyBackups/{uid} never did, e.g. setup got interrupted between the two
// writes in completeSetup; the user then sees THIS modal forever with no
// backup that could ever successfully unlock). There was no way out other
// than re-reading the text "you'll be able to start fresh once a new key is
// generated" with no actual control to do that.
//
// Clicking "Forgotten your passphrase?" below switches this modal into a
// second mode that looks like SetupChatModal — new passphrase + confirm +
// explicit acknowledgement that old encrypted chat history becomes
// permanently unreadable — and calls `onForgotKey` (wired by ChatKeyGate.js
// to the same completeSetup used for first-run setup; its writes are
// idempotent merges, so it's safe to call again here and simply overwrites
// whatever key/backup existed before with a fresh one).
//
// Both modes have a close (X) button (`onDismiss`) that closes the chat
// drawer and returns to the rest of the CRM without unlocking/resetting —
// previously omitted for the same "nothing chat-related can open here yet"
// reasoning as SetupChatModal, but that left users with no way out if they
// didn't have their passphrase on hand. Dismissing doesn't change
// chatKeyStatus, so this modal reappears next time chat is opened.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const MIN_PASSPHRASE_LENGTH = 8;

function LockIcon() {
  return (
    <div style={{
      width: 56, height: 56, borderRadius: 16, marginBottom: 14,
      background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    </div>
  );
}

function CloseButton({ onDismiss }) {
  if (!onDismiss) return null;
  return (
    <button
      type="button"
      onClick={onDismiss}
      title="Close and return to CRM"
      style={{
        position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: 8,
        background: 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)',
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>
  );
}

export default function UnlockChatModal({ onComplete, onForgotKey, onDismiss }) {
  const [mode, setMode] = useState('unlock'); // 'unlock' | 'reset'

  // ── "unlock" mode state ──────────────────────────────────────────────────
  const [passphrase, setPassphrase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // ── "reset" (forgot key) mode state ──────────────────────────────────────
  const [newPassphrase, setNewPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    if (!passphrase || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onComplete(passphrase);
    } catch (err) {
      console.error('[UnlockChatModal]', err);
      setError(err.message === 'Incorrect recovery passphrase'
        ? 'Incorrect recovery passphrase — please try again.'
        : (err.message || 'Could not unlock chat. Please try again.'));
      setSubmitting(false);
    }
  };

  const tooShort = newPassphrase.length > 0 && newPassphrase.length < MIN_PASSPHRASE_LENGTH;
  const mismatch = confirm.length > 0 && newPassphrase !== confirm;
  const canReset = newPassphrase.length >= MIN_PASSPHRASE_LENGTH && newPassphrase === confirm && acknowledged && !resetSubmitting;

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!canReset) return;
    setResetSubmitting(true);
    setResetError('');
    try {
      await onForgotKey(newPassphrase);
    } catch (err) {
      console.error('[UnlockChatModal:reset]', err);
      setResetError(err.message || 'Something went wrong generating a new chat key. Please try again.');
      setResetSubmitting(false);
    }
    // On success the parent unmounts this modal (status flips to 'ready').
  };

  if (mode === 'reset') {
    return createPortal(
      <div className="modal-overlay">
        <div className="modal-box" style={{ maxWidth: 480, position: 'relative' }}>
          <CloseButton onDismiss={onDismiss} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 20 }}>
            <LockIcon />
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Generate a new chat key</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
              This replaces your old chat key with a brand-new one and sets a fresh
              passphrase. Your old encrypted messages will no longer be readable by
              anyone, on any device — this can't be undone.
            </p>
          </div>

          <form onSubmit={handleResetSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                New Chat Recovery Passphrase
              </label>
              <input
                type="password"
                className="crm-input"
                placeholder="At least 8 characters"
                value={newPassphrase}
                onChange={e => setNewPassphrase(e.target.value)}
                autoComplete="new-password"
                disabled={resetSubmitting}
                autoFocus
              />
              {tooShort && (
                <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 6 }}>
                  Use at least {MIN_PASSPHRASE_LENGTH} characters.
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                Confirm New Passphrase
              </label>
              <input
                type="password"
                className="crm-input"
                placeholder="Re-enter your new passphrase"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                disabled={resetSubmitting}
              />
              {mismatch && (
                <div style={{ fontSize: 12, color: '#f87171', marginTop: 6 }}>
                  Passphrases don't match.
                </div>
              )}
            </div>

            <div style={{
              background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 10, padding: '10px 12px', marginBottom: 16, fontSize: 12.5,
              color: 'var(--text-secondary)', lineHeight: 1.5,
            }}>
              <strong style={{ color: '#f87171' }}>This permanently erases access to your old chat history</strong> on
              every device — including this one. New messages going forward will be encrypted
              under the new key.
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 18, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={e => setAcknowledged(e.target.checked)}
                disabled={resetSubmitting}
                style={{ marginTop: 2 }}
              />
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                I understand my old encrypted chat history will be permanently unreadable after this.
              </span>
            </label>

            {resetError && (
              <div style={{ fontSize: 12.5, color: '#f87171', marginBottom: 14 }}>{resetError}</div>
            )}

            <button type="submit" className="btn-primary" disabled={!canReset} style={{ width: '100%', justifyContent: 'center' }}>
              {resetSubmitting ? 'Generating…' : 'Generate new chat key'}
            </button>

            <button
              type="button"
              onClick={() => { setMode('unlock'); setResetError(''); }}
              disabled={resetSubmitting}
              style={{
                width: '100%', marginTop: 10, background: 'none', border: 'none',
                color: 'var(--text-muted)', fontSize: 12.5, cursor: 'pointer', textAlign: 'center',
              }}
            >
              ← Back to unlock with my existing passphrase
            </button>
          </form>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 440, position: 'relative' }}>
        <CloseButton onDismiss={onDismiss} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 20 }}>
          <LockIcon />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Unlock your chat</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
            This device doesn't have your chat key yet. Enter your Chat Recovery
            Passphrase to unlock your conversations here.
          </p>
        </div>

        <form onSubmit={handleUnlockSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 8 }}>
              Chat Recovery Passphrase
            </label>
            <input
              type="password"
              className="crm-input"
              placeholder="Enter your passphrase"
              value={passphrase}
              onChange={e => { setPassphrase(e.target.value); setError(''); }}
              autoComplete="current-password"
              disabled={submitting}
              autoFocus
            />
          </div>

          {error && (
            <div style={{ fontSize: 12.5, color: '#f87171', marginBottom: 14 }}>{error}</div>
          )}

          <button type="submit" className="btn-primary" disabled={!passphrase || submitting} style={{ width: '100%', justifyContent: 'center' }}>
            {submitting ? 'Unlocking…' : 'Unlock chat'}
          </button>

          <button
            type="button"
            onClick={() => { setMode('reset'); setError(''); }}
            disabled={submitting}
            style={{
              width: '100%', marginTop: 14, background: 'none', border: 'none',
              color: 'var(--text-muted)', fontSize: 11.5, cursor: 'pointer', textAlign: 'center',
              textDecoration: 'underline', lineHeight: 1.5,
            }}
          >
            Forgotten your passphrase? Generate a new chat key
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
